!function () {
  "use strict";

  // Product Model (AR / 3D Viewer)
  class ProductModelElement extends window.theme.DeferredMedia {
    constructor() {
      super();
    }

    loadContent() {
      super.loadContent();
      Shopify.loadFeatures([
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    }

    setupModelViewerUI(error) {
      if (error) return;
      this.modelViewerUI = new Shopify.ModelViewerUI(
        this.querySelector("model-viewer")
      );
    }
  }

  // Shopify XR
  window.ProductModel = {
    loadShopifyXR() {
      Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this.setupShopifyXR.bind(this),
        },
      ]);
    },

    setupShopifyXR(error) {
      if (error) return;

      if (window.ShopifyXR) {
        document.querySelectorAll('[id^="ModelJSON-"]').forEach((el) => {
          window.ShopifyXR.addModels(JSON.parse(el.textContent));
          el.remove();
        });

        window.ShopifyXR.setupXRElements();
      } else {
        document.addEventListener("shopify_xr_initialized", () =>
          this.setupShopifyXR()
        );
      }
    },
  };

  window.addEventListener("DOMContentLoaded", () => {
    if (window.ProductModel) {
      window.ProductModel.loadShopifyXR();
    }
  });

  // Constants
  const SELECTOR_ADD_TO_CART = "[data-add-to-cart]";
  const SELECTOR_PRODUCT_JSON = "[data-product-json]";
  const SELECTOR_PRODUCT_FORM = "[data-product-form]";
  const SELECTOR_CART_BAR = "#cart-bar";
  const SELECTOR_POPUP_OPEN = "[data-popup-open]";
  const SELECTOR_CART_BAR_BTN = ".product__submit__add";
  const SELECTOR_FORM_WRAPPER = "[data-form-wrapper]";
  const SELECTOR_PRODUCT_VARIANTS = "[data-product-variants]";

  const CLASS_LOADING = "is-loading";
  const CLASS_VISIBLE = "is-visible";

  const ATTR_CART_BAR_ENABLED = "data-cart-bar-enabled";
  const ATTR_ADD_TO_CART_BAR = "data-add-to-cart-bar";
  const ATTR_CART_BAR_SCROLL = "data-cart-bar-scroll";
  const ATTR_CART_BAR_NOTIFICATION = "data-cart-bar-product-notification";
  const ATTR_STICKY_ENABLED = "data-sticky-enabled";

  // Product Component
  if (!customElements.get("product-component")) {
    customElements.define(
      "product-component",
      class extends HTMLElement {
        constructor() {
          super();

          this._selectors = {
            relatedColors: '[js-related-colors]',
            relatedSwatches: '[js-related-swatches]'
          }

          this.stickyEnabled = this.getAttribute(ATTR_STICKY_ENABLED) === "true";
          this.formWrapper = this.querySelector(SELECTOR_FORM_WRAPPER);
          this.cartBarEnabled = this.hasAttribute(ATTR_CART_BAR_ENABLED);
          this.cartBar = this.querySelector(SELECTOR_CART_BAR);

          this.setCartBarHeight = this.setCartBarHeight.bind(this);
          this.scrollToTop = this.scrollToTop.bind(this);
          this.toggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);

          this.unlockTimer = 0;
        }

        connectedCallback() {
          const productJsonEl = this.querySelector(SELECTOR_PRODUCT_JSON);

          if (!productJsonEl || !productJsonEl.innerHTML) return;

          const handle = JSON.parse(productJsonEl.innerHTML).handle;
          let data = {};

          if (handle) {
            data = { handle };
          }

          Shopify.Products.recordRecentlyViewed(data);
          Shopify.Products &&
            Shopify.Products.recordRecentlyViewed &&
            Shopify.Products.recordRecentlyViewed(data);

          this.form = this.querySelector(SELECTOR_PRODUCT_FORM);

          if (this.cartBarEnabled) {
            this.initCartBar();
            this.setCartBarHeight();
            document.addEventListener("theme:scroll", this.toggleCartBarOnScroll);
            document.addEventListener("theme:resize", this.setCartBarHeight);
          }

          this._initRelatedColors();
          this._initRelatedSwatches();
        }

        initCartBar() {
          this.cartBarBtns = this.cartBar.querySelectorAll(SELECTOR_CART_BAR_BTN);

          if (this.cartBarBtns.length > 0) {
            this.cartBarBtns.forEach((btn) => {
              btn.addEventListener("click", (e) => {
                e.preventDefault();

                if (e.currentTarget.hasAttribute(ATTR_ADD_TO_CART_BAR)) {
                  if (this.cartBarEnabled) {
                    e.currentTarget.classList.add(CLASS_LOADING);
                    e.currentTarget.setAttribute("disabled", "disabled");
                  }
                  this.form
                    .querySelector(SELECTOR_ADD_TO_CART)
                    .dispatchEvent(new Event("click", { bubbles: true }));
                } else if (e.currentTarget.hasAttribute(ATTR_CART_BAR_SCROLL)) {
                  this.scrollToTop();
                } else if (e.currentTarget.hasAttribute(ATTR_CART_BAR_NOTIFICATION)) {
                  this.form.querySelector(SELECTOR_POPUP_OPEN)?.dispatchEvent(
                    new Event("click")
                  );
                }
              });

              if (btn.hasAttribute(ATTR_ADD_TO_CART_BAR)) {
                document.addEventListener(
                  "theme:product:add-error",
                  this.scrollToTop
                );
              }
            });

            this.setCartBarHeight();
          }
        }

        scrollToTop() {
          const variantsEl = this.querySelector(SELECTOR_PRODUCT_VARIANTS);
          const target = window.theme.isMobile()
            ? variantsEl || this.form
            : this;

          const offset = target.getBoundingClientRect().top;
          window.theme.scrollTo(window.theme.isMobile() ? offset - 10 : offset);
        }

        toggleCartBarOnScroll() {
          const scrollY = window.scrollY;
          const formRef =
            theme.variables.productPageSticky && this.formWrapper
              ? this.formWrapper
              : this.form;

          if (formRef && this.cartBar) {
            const shouldShow =
              scrollY > formRef.offsetTop + formRef.offsetHeight;
            this.cartBar.classList.toggle(CLASS_VISIBLE, shouldShow);
          }
        }

        setCartBarHeight() {
          const height = this.cartBar.offsetHeight;
          document.documentElement.style.setProperty(
            "--cart-bar-height",
            `${height}px`
          );
        }

        async _initRelatedColors() {
          this.relatedColorsContainer = this.querySelector(this._selectors.relatedColors);
          if (!this.relatedColorsContainer) {
            return;
          }
      
          const currentProductHandle = this.relatedColorsContainer.getAttribute('data-handle');
          const relatedColorsTag = this.relatedColorsContainer.getAttribute('data-tag');
          const targetURL = `/collections/product-grouping/${relatedColorsTag}/?view=json`;
          const relatedColorsJSON = await this._getRelatedProductsJSON(targetURL);

          let relatedColorsMarkup = '';
      
          if (relatedColorsJSON.length <= 1) {
            return;
          }

          relatedColorsJSON.forEach((product) => {
            if (product.productHandle == currentProductHandle) {
              relatedColorsMarkup = `${relatedColorsMarkup}
              <div class="swatch__element swatch__element--selected">
                <div class="swatch__label swatch__label--color" style="${product.swatch_image ? `background-image: url(${product.swatch_image})` : 'background-color: #000'}"></div>
              </div>`
            }else{
              relatedColorsMarkup = `${relatedColorsMarkup}
              <a href="${product.productUrl}" class="swatch__element swatch__trigger" aria-label="${product.swatch_color}">
                <div class="swatch__label swatch__label--color" style="${product.swatch_image ? `background-image: url(${product.swatch_image})` : 'background-color: #000'}"></div>
              </a>`
            }
          })
      
          this.relatedColorsContainer.innerHTML = relatedColorsMarkup;
          this.relatedColorsContainer.classList.remove('hidden');
        }

        async _initRelatedSwatches() {
          this.relatedSwatchesContainer = this.querySelector(this._selectors.relatedSwatches);

          if (!this.relatedSwatchesContainer) return;
      
          const currentProductHandle = this.relatedSwatchesContainer.getAttribute('data-handle');
          const relatedSwatchesTag = this.relatedSwatchesContainer.getAttribute('data-tag');
          const targetURL = `/collections/product-grouping/${relatedSwatchesTag}/?view=json`;
          const relatedSwatchesJSON = await this._getRelatedProductsJSON(targetURL);

          let relatedSwatchesMarkup = '';
      
          if (relatedSwatchesJSON.length <= 1) {
            return;
          }

          relatedSwatchesJSON.forEach((product) => {
            if (product.productHandle == currentProductHandle) {
              relatedSwatchesMarkup = `${relatedSwatchesMarkup}
              <div class="swatch__text-element swatch__text-element--selected">
                ${product.additional_swatch_value}
              </div>`
            }else{
              relatedSwatchesMarkup = `${relatedSwatchesMarkup}
              <a href="${product.productUrl}" class="swatch__text-element swatch__text-trigger">
                ${product.additional_swatch_value}
              </a>`
            }
          })
      
          this.relatedSwatchesContainer.innerHTML = relatedSwatchesMarkup;
          this.relatedSwatchesContainer.classList.remove('hidden');
        }        
      
        _getRelatedProductsJSON(url) {
          return fetch(url)
            .then(response => response.text())
            .then((text) => {
              const html = text;
              const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
              const JSONcontainer = parsedHTML.querySelector('[js-collection-json]');
              const parsedJSON = JSON.parse(JSONcontainer.innerHTML);
              return parsedJSON;
            })
        }

        disconnectedCallback() {
          document.removeEventListener(
            "theme:product:add-error",
            this.scrollToTop
          );

          if (this.cartBarEnabled) {
            document.removeEventListener(
              "theme:scroll",
              this.toggleCartBarOnScroll
            );
            document.removeEventListener("theme:resize", this.setCartBarHeight);
          }
        }
      }
    );
  }

  if (!customElements.get("product-model")) {
    customElements.define("product-model", ProductModelElement);
  }
}();